import { useQuery } from '@tanstack/react-query'
import { useEffect } from 'react'
import { getMeFactorConfiguration, getMeFactorSettings, getFeatureFlags } from 'api/factors'
import { LS_FACTOR_SETTINGS } from 'constants/localStorageKeys'
import { qkGetFactorConfiguration, qkGetFactorSettings, qkGetFeatureFlags } from 'constants/QueryKeys/factor'
import { booleanToString } from 'tools/common'

const useAdminSettings = () => {
  const { data: factorMeSettingResponse, refetch: refetchFactorSettings } = useQuery({
    queryKey: qkGetFactorSettings('me'),
    queryFn: () => {
      return getMeFactorSettings()
    },
  })

  const { data: factorConfigurationResponse, refetch: refetchFactorConfiguration } = useQuery({
    queryKey: qkGetFactorConfiguration(),
    queryFn: () => {
      return getMeFactorConfiguration()
    },
  })

  const { data: featureFlagsResponse } = useQuery({
    queryKey: qkGetFeatureFlags(),
    queryFn: () => {
      return getFeatureFlags()
    },
  })

  const brightboltIntegration =
    featureFlagsResponse?.brightbolt.enabled && factorMeSettingResponse?.settings?.enableBrightboltIntegration

  const overAdvancesFlagEnabled = featureFlagsResponse?.overAdvances?.enabled ?? true
  const factorEnableOverAdvances = factorMeSettingResponse?.settings.enableOverAdvances || false
  const overAdvancesEnabled = overAdvancesFlagEnabled && factorEnableOverAdvances
  const openBalanceOverAdvancesEnabled =
    overAdvancesEnabled && (factorMeSettingResponse?.settings.enableOpenBalanceOverAdvances ?? false)
  const flagEnableCurrencyFilter = featureFlagsResponse?.currencyFilter?.enabled || false
  const factorEnableCurrencyFilter = factorConfigurationResponse?.factorConfiguration.enableCurrencyFilter || false
  const showCurrencyFilter = flagEnableCurrencyFilter && factorEnableCurrencyFilter
  // FC2-4184: gated directly by factor configuration, no separate platform-level feature flag.
  const enableMultipleFundingInstructions =
    factorConfigurationResponse?.factorConfiguration.enableMultipleFundingInstructions || false

  const enableCurrencySymbolSetting = factorMeSettingResponse?.settings.enableCurrencySymbol
  useEffect(() => {
    if (!factorMeSettingResponse || !factorConfigurationResponse || !featureFlagsResponse) return
    localStorage.setItem(
      LS_FACTOR_SETTINGS,
      JSON.stringify({
        enableCurrencySymbol: booleanToString(enableCurrencySymbolSetting),
        enableCurrencyFilter: booleanToString(showCurrencyFilter),
      })
    )
  }, [
    factorMeSettingResponse,
    factorConfigurationResponse,
    featureFlagsResponse,
    enableCurrencySymbolSetting,
    showCurrencyFilter,
  ])
  const enableFuelAdvanceCredit =
    featureFlagsResponse?.fuelAdvanceCredit?.enabled && factorMeSettingResponse?.settings.enableFuelAdvanceCredit
  const enableSalesExecutiveEffectiveDates = featureFlagsResponse?.salesExecutiveEffectiveDates?.enabled || false
  const enableCollectionsV2 = factorMeSettingResponse?.settings?.enableCollectionsV2 || false
  const enableFeeEscrow =
    (featureFlagsResponse?.feeEscrow?.enabled ?? false) &&
    (factorConfigurationResponse?.factorConfiguration.enableFeeEscrow ?? false)
  const enableOverPaymentInvoiceCredit =
    (featureFlagsResponse?.overPaymentInvoiceCredit?.enabled ?? false) &&
    (factorConfigurationResponse?.factorConfiguration.enableOverPaymentInvoiceCredit ?? false)
  const enableThirdParty =
    (featureFlagsResponse?.thirdPartyFees?.enabled ?? false) &&
    (factorConfigurationResponse?.factorConfiguration.enableThirdPartyFees ?? false)
  const enableCustomGlCodes = factorMeSettingResponse?.settings.customGlCodesEnabled || false
  const refetchMeAdminSettings = () => {
    void refetchFactorSettings()
    void refetchFactorConfiguration()
  }

  return {
    chargeAccruedInterest: factorMeSettingResponse?.settings.chargeAccruedInterest,
    enableFuelAdvance: factorMeSettingResponse?.settings.enableFuelAdvance,
    flagEnableBrokerMod: featureFlagsResponse?.brokerMod.enabled,
    enableBrokerMod: factorMeSettingResponse?.settings.enableBrokerMod,
    flagEnableCurrencyFilter: flagEnableCurrencyFilter,
    enableCurrencyFilter: factorEnableCurrencyFilter,
    enabledCurrencies: factorConfigurationResponse?.factorConfiguration.enabledCurrencies,
    showCurrencyFilter: showCurrencyFilter,
    factorFrequencyOfCollectionOfAccruedFees:
      factorMeSettingResponse?.settings.factorFrequencyOfCollectionOfAccruedFees,
    enableBrightboltIntegration: brightboltIntegration,
    hideAllChargebacks: featureFlagsResponse?.hideAllChargebacks.enabled,
    refetchMeAdminSettings: refetchMeAdminSettings,
    factorSetting: factorMeSettingResponse?.settings,
    factorIntegrations: factorMeSettingResponse?.integrations,
    exportLedgers: featureFlagsResponse?.exportLedgers.enabled || false,
    exportLedgersMaxRows: featureFlagsResponse?.exportLedgers.maxRows || 10,
    editPayment: featureFlagsResponse?.editPayment.enabled || false,
    enableNonFactoredInvoices: featureFlagsResponse?.nonFactoredInvoices.enabled || false,
    //enableNonFactoredInvoices: false,
    enableWithdrawPayment: featureFlagsResponse?.undoPayment.enabled || false,
    //enableWithdrawPayment: false,
    enableCreditCheck: featureFlagsResponse?.creditCheck.enabled || false,
    //enableCreditCheck: false,
    enableOutOfSystemInvoices: featureFlagsResponse?.outOfSystemInvoices.enabled || false,
    //enableOutOfSystemInvoices: false,
    enableReserveReleases: featureFlagsResponse?.reserveReleases.enabled || false,
    //enableReserveReleases: false,
    enableRebillInvoices: featureFlagsResponse?.rebillInvoices.enabled || false,
    //rebillInvoices: false,
    enableNoaLevelSetting: featureFlagsResponse?.noaLevelSetting.enabled || false,
    //enableNoaLevelSetting: false,
    noaLevelSetting: factorMeSettingResponse?.settings.noaLevelSetting,
    enableSplitPayments: featureFlagsResponse?.splitPayments.enabled || false,
    //enableSplitPayments: false,

    //enableAutomatedFunding: false,
    enableAutomatedFunding: featureFlagsResponse?.automatedFunding.enabled || false,
    enableInvoiceLineItem: factorMeSettingResponse?.settings.enableInvoiceLineItem,

    allowPaymentBackdating: factorMeSettingResponse?.settings.allowPaymentBackdating || false,
    enableNachaFile: factorMeSettingResponse?.settings.enableNachaFile || false,
    enableFactorAutoDraftPayments: factorMeSettingResponse?.settings.enableAutoDraftPayments || false,
    enableDocumentPaymentCreation: featureFlagsResponse?.automatedDraftPayments.enabled || false,
    //hideFundingChargebacks: false,
    hideFundingChargebacks: featureFlagsResponse?.hideFundingChargebacks.enabled || false,
    //enablePreferredPaymentChannel: false,
    enablePreferredPaymentChannel: featureFlagsResponse?.preferredPaymentChannel.enabled || false,
    enableCreditSafe: factorMeSettingResponse?.settings.enableCreditSafe || false,

    collectionFeeMethod: factorMeSettingResponse?.settings.collectionFeeMethod || 'CASH_BASED',
    flagEnableOverAdvances: overAdvancesFlagEnabled,
    enableOverAdvances: overAdvancesEnabled,
    enableOpenBalanceOverAdvances: openBalanceOverAdvancesEnabled,
    flagEnableWriteOffs: featureFlagsResponse?.writeOffs.enabled || false,
    enableWriteOffs: factorMeSettingResponse?.settings.enableWriteOffs || false,
    dueDateRequired: factorMeSettingResponse?.settings?.dueDateRequired || false,
    costOfFunds: featureFlagsResponse?.costOfFunds?.enabled || false,

    // Bland AI Integration
    blandAiEnabled: factorMeSettingResponse?.settings.blandAiEnabled || false,

    // Fuel Advance Credit
    flagEnableFuelAdvanceCredit: featureFlagsResponse?.fuelAdvanceCredit?.enabled || false,
    enableFuelAdvanceCredit: enableFuelAdvanceCredit || false,

    enableSalesExecutiveEffectiveDates: enableSalesExecutiveEffectiveDates || false,

    enableCollectEscrowFirst: factorMeSettingResponse?.settings.enableCollectEscrowFirst || false,

    portalDuctEnabled: factorMeSettingResponse?.settings?.portalDuctEnabled || false,
    enableCollectionsV2: enableCollectionsV2 || false,
    enableCommissionPartners: featureFlagsResponse?.commissionPartners?.enabled || false,
    allowMixedTypePaymentSelection: featureFlagsResponse?.allowMixedTypePaymentSelection?.enabled ?? false,
    flagFeeEscrow: featureFlagsResponse?.feeEscrow?.enabled ?? false,
    enableFeeEscrow: enableFeeEscrow,
    flagOverPaymentInvoiceCredit: featureFlagsResponse?.overPaymentInvoiceCredit?.enabled ?? false,
    enableOverPaymentInvoiceCredit: enableOverPaymentInvoiceCredit,
    flagAutoFundingPaymentType: featureFlagsResponse?.autoFundingPaymentType?.enabled ?? false,
    enableCustomGlCodes: enableCustomGlCodes,
    enablePaymentBatchReconciliation: factorMeSettingResponse?.settings.enablePaymentBatchReconciliation || false,
    autoAttachFuelAdvanceInvoice: factorMeSettingResponse?.settings.autoAttachFuelAdvanceInvoice ?? false,
    autoAttachFuelAdvanceCredit: factorMeSettingResponse?.settings.autoAttachFuelAdvanceCredit ?? false,
    autoAttachOverAdvance: factorMeSettingResponse?.settings.autoAttachOverAdvance ?? false,
    enableMultipleFundingInstructions: enableMultipleFundingInstructions,
    flagPortfolioRisk: featureFlagsResponse?.portfolioRisk?.enabled ?? false,
    flagThirdParty: featureFlagsResponse?.thirdPartyFees?.enabled ?? false,
    enableThirdParty: enableThirdParty,
  }
}

export default useAdminSettings
